/**
 * Module dependencies,
 */

import { Button } from 'src/components/core/buttons/button';
import { Image } from 'src/components/core/image';
import { RawHtml } from 'src/components/core/raw-html';
import { Text } from 'src/components/core/text';
import { media } from 'src/styles/utils/media';
import buttonIcon from 'src/assets/svg/button.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  buttonLabel: string;
  buttonLink: string;
  description: string;
  images: [string, string];
  price?: string;
  priceDefaultLabel?: string;
  secondDescription?: string;
  secondTitle?: string;
  title: string;
};
/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  --grid-header-vertical-spacing: 0;
  --grid-header-title-vertical-spacing: 0;

  display: grid;
  grid-gap: 2rem;
  grid-template-columns: repeat(10, 1fr);
  grid-template-rows:
    repeat(2, min-content)
    var(--grid-header-vertical-spacing)
    calc(var(--grid-header-vertical-spacing) * 2)
    var(--grid-header-vertical-spacing)
    repeat(2, min-content)
    var(--grid-header-vertical-spacing);

  ${media.max.ms`
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, min-content) max-content repeat(2, min-content);
  `}

  ${media.max.md`
    padding-bottom: 3rem;
  `}

  ${media.min.md`
    --grid-header-title-vertical-spacing: 3rem;
    --grid-header-vertical-spacing: var(--vertical-spacing);

    grid-gap: 0;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  grid-column: 1 / -1;
  grid-row: 1 / span 1;

  ${media.min.ms`
    grid-column: 1 / span 11;
    padding-right: 2rem;
  `}

  ${media.min.md`
    grid-column: 1 / span 5;
    margin-bottom: 2rem;
    padding-right: 1rem;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({ variant: 'display4' })`
  hyphens: auto;
`;

/**
 * `Description` styled component.
 */

const Description = styled.div`
  grid-column: 1 / -1;
  grid-row: 2 / span 1;

  ${media.min.ms`
    grid-column: 1 / span 4;
    padding-right: 2rem;
  `}

  ${media.min.md`
    grid-column: 2 / span 4;
    padding-right: 1rem;
  `}
`;

/**
 * `MainImage` styled component.
 */

const MainImage = styled.div`
  grid-column: 1 / -1;
  grid-row: 3 / span 1;
  overflow: hidden;
  padding-top: 130%;
  position: relative;

  ${media.min.ms`
    grid-column: 6 / span 5;
    grid-row: 2 / span 4;
    padding-top: 0;
  `}

  ${media.min.md`
    grid-row: 1 / span 4;
  `}
`;

/**
 * `SecondaryImage` styled component.
 */

const SecondaryImage = styled.div`
  grid-column: 1 / -1;
  grid-row: 5 / span 1;
  overflow: hidden;
  padding-top: 112%;
  position: relative;

  ${media.min.ms`
    grid-column: 1 / span 5;
    grid-row: 4 / span 4;
    margin-right: 24px;
  `}

  ${media.min.lg`
    grid-column: 1 / span 4;
    grid-row: 4 / span 3;
    margin-right: 0;
  `}
`;

/**
 * `WhatIsIt` styled component.
 */

const WhatIsIt = styled.div`
  grid-column: 1 / -1;
  grid-row: 4 / span 1;
  padding-top: 3rem;

  ${media.min.ms`
    grid-column: 6 / span 5;
    grid-row: 6 / span 2;
    padding-top: 0;
  `}
`;

/**
 * `GridHeader` component.
 */

export function GridHeader(props: Props) {
  const {
    buttonLabel,
    buttonLink,
    description,
    images,
    price,
    priceDefaultLabel,
    secondDescription,
    secondTitle,
    title
  } = props;

  return (
    <Grid>
      <TitleWrapper>
        <Title
          as={'h1'}
          data-scroll={'fade-in-up'}
          data-scroll-animation-delay={100}
        >
          {title}
        </Title>

        {price ? (
          <Text
            block
            data-scroll={'fade-in-up'}
            data-scroll-animation-delay={200}
          >
            {price}
          </Text>
        ) : (
          <Text
            block
            data-scroll={'fade-in-up'}
            data-scroll-animation-delay={200}
          >
            {priceDefaultLabel}
          </Text>
        )}
      </TitleWrapper>

      <Description>
        <Text
          block
          data-scroll={'fade-in-up'}
          data-scroll-animation-delay={200}
          style={{ marginBottom: '3rem' }}
          variant={'subtitle2'}
        >
          <RawHtml>{description}</RawHtml>
        </Text>

        <Button
          colorTheme={'greenOutlined'}
          data-scroll={'fade-in-up'}
          data-scroll-animation-delay={300}
          data-scroll-delay={0.6}
          data-scroll-direction={'vertical'}
          data-scroll-speed={-1}
          href={buttonLink}
          icon={buttonIcon}
        >
          {buttonLabel}
        </Button>
      </Description>

      <MainImage>
        <Image
          alt={'Main image'}
          data-scroll={'clip-left'}
          data-scroll-animation-delay={500}
          data-scroll-delay={0.66}
          data-scroll-direction={'vertical'}
          data-scroll-speed={-1}
          fill
          src={images[0]}
        />
      </MainImage>

      <SecondaryImage>
        <Image
          alt={'Secondary Image'}
          data-scroll={'clip-left'}
          data-scroll-animation-delay={300}
          data-scroll-delay={0.66}
          data-scroll-direction={'vertical'}
          data-scroll-speed={-1}
          fill
          src={images[1]}
        />
      </SecondaryImage>

      {(secondTitle || secondDescription) && (
        <WhatIsIt>
          {secondTitle && (
            <Text
              block
              data-scroll={'fade-in-up'}
              data-scroll-offset={'20%'}
              style={{ marginBottom: '2.5rem' }}
              variant={'h2'}
            >
              <RawHtml>{secondTitle}</RawHtml>
            </Text>
          )}

          {secondDescription && (
            <Text
              data-scroll={'fade-in-up'}
              data-scroll-animation-delay={200}
              data-scroll-offset={'20%'}
              variant={'subtitle2'}
            >
              <RawHtml>{secondDescription}</RawHtml>
            </Text>
          )}
        </WhatIsIt>
      )}
    </Grid>
  );
}
